import { MapPin, Users, BadgeCheck } from "lucide-react";
import { Overline, Reveal, MaskLine } from "./shared";
import { useLang } from "@/i18n";

const factIcons = [Users, MapPin, BadgeCheck];
const factIds = ["members", "location", "fssai"];

const moments = [
  { src: "/assets/farm/moment-winnowing.jpeg", alt: "Winnowing and cleaning mustard seeds by hand" },
  { src: "/assets/farm/moment-copra.jpeg", alt: "Breaking copra for coconut oil" },
  { src: "/assets/farm/moment-weighing.jpeg", alt: "Weighing every filled bottle before sealing" },
];

export default function Community() {
  const { t } = useLang();
  const facts = t("community.facts");

  return (
    <section id="community" className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32" data-testid="community-section">
      <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12">
        <div className="relative lg:col-span-6">
          <Reveal>
            <div className="overflow-hidden border border-ink/10">
              <img
                src="/assets/farm/community-group.jpeg"
                alt="The women of Sira Shakti Aajivika Producer Group at their wood press unit"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.2} className="absolute -bottom-10 right-0 hidden w-56 sm:block lg:-right-8">
            <div className="arch-frame overflow-hidden border-4 border-cream shadow-xl">
              <img
                src="/assets/farm/community-filtering.jpeg"
                alt="Producers filtering freshly pressed oil through cloth"
                className="aspect-[3/4] w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:pl-6">
          <Overline>{t("community.overline")}</Overline>
          <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            <MaskLine>{t("community.title1")}</MaskLine>
            <MaskLine delay={0.12}>
              <span className="italic text-terracotta">{t("community.title2")}</span>
            </MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-moss">
              {t("community.body")}
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <ul className="mt-10 space-y-5">
              {facts.map((f, i) => {
                const Icon = factIcons[i];
                return (
                  <li key={factIds[i]} className="flex items-start gap-4" data-testid={`community-fact-${factIds[i]}`}>
                    <Icon className="mt-1 h-5 w-5 shrink-0 text-terracotta" />
                    <p className="text-sm font-light text-moss">
                      <span className="font-medium text-ink">{f.strong}</span>{f.rest}
                    </p>
                  </li>
                );
              })}
            </ul>
          </Reveal>

          <Reveal delay={0.4}>
            <blockquote className="mt-10 border-l-2 border-ochre pl-6 font-serif text-xl italic leading-relaxed text-ink sm:text-2xl">
              {t("community.quote")}
            </blockquote>
          </Reveal>
        </div>
      </div>

      <Reveal className="mt-20 lg:mt-28">
        <div className="grid grid-cols-3 gap-4 lg:gap-6" data-testid="community-moments">
          {moments.map((m, i) => (
            <div key={m.src} className={`overflow-hidden border border-ink/10 ${i === 1 ? "mt-6 lg:mt-10" : ""}`}>
              <img
                src={m.src}
                alt={m.alt}
                className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                loading="lazy"
              />
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs uppercase tracking-[0.18em] text-moss">{t("community.momentsCaption")}</p>
      </Reveal>
    </section>
  );
}
